import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { css, html } from '../../lib/lit.js';
import { router } from '../../lib/router.js';

class ChannelProductsView extends BaseComponent {
    static properties = {
        _channelProducts: { type: Array, state: true },
        _products: { type: Array, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _view: { type: String, state: true },
        _formData: { type: Object, state: true },
        _saving: { type: Boolean, state: true },
        _confirmDelete: { type: String, state: true },
    };

    static styles = css`
        :host { display: block; }
    `;

    constructor() {
        super();
        this._channelProducts = [];
        this._products = [];
        this._loading = true;
        this._error = null;
        this._view = 'list';
        this._formData = { discord_channel_id: '', product: '' };
        this._saving = false;
        this._confirmDelete = null;
    }

    async connectedCallback() {
        super.connectedCallback();
        this._unsubscribeRouter = router.subscribe((state) => {
            if (state.path === '/channel-products/new') {
                this._view = 'create';
                this._formData = { discord_channel_id: '', product: '' };
            } else {
                this._view = 'list';
            }
        });
        this._loadData();
        if (window.location.pathname === '/channel-products/new') {
            this._view = 'create';
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._unsubscribeRouter) this._unsubscribeRouter();
    }

    async _loadData() {
        this._loading = true;
        this._error = null;
        try {
            const [channelProducts, products] = await Promise.all([
                api.get('/api/channel-products'),
                api.get('/api/community-products'),
            ]);
            this._channelProducts = channelProducts;
            this._products = products;
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    _productName(slug) {
        const product = this._products.find((p) => p.slug === slug);
        return product ? product.name : slug;
    }

    async _submitCreate(e) {
        e.preventDefault();
        this._saving = true;
        this._error = null;
        try {
            await api.post('/api/channel-products', this._formData);
            await this._loadData();
            router.navigate('/channel-products');
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _deleteChannelProduct(id) {
        this._error = null;
        try {
            await api.delete(`/api/channel-products/${id}`);
            this._confirmDelete = null;
            await this._loadData();
        } catch (err) {
            this._error = err.message;
        }
    }

    _updateField(field, value) {
        this._formData = { ...this._formData, [field]: value };
    }

    render() {
        if (this._view === 'create') return this._renderForm();
        return this._renderList();
    }

    _renderList() {
        return html`
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0">Channel Products</h5>
                <a href="/channel-products/new" class="btn btn-primary btn-sm">
                    <i class="bi bi-plus-lg"></i> Add Mapping
                </a>
            </div>

            <p class="text-muted" style="font-size: 0.875rem;">
                The community product feedback filed in a channel is about when nobody names
                one, as in <code>@d3po bug the dice tray clips</code>. A channel with no mapping
                files with no product, and staff assign one on the portal. Naming an app
                (<code>@d3po bug in vtt …</code>) always wins over the mapping.
            </p>

            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}

            ${
                this._loading
                    ? html`
                <div class="text-center py-4">
                    <div class="spinner-border spinner-border-sm text-primary"></div>
                </div>
            `
                    : html`
                ${
                    this._channelProducts.length === 0
                        ? html`
                    <div class="text-muted text-center py-4">No channel products configured.</div>
                `
                        : html`
                    <table class="table table-hover">
                        <thead>
                            <tr>
                                <th>Channel ID</th>
                                <th>Product</th>
                                <th style="width: 80px;"></th>
                            </tr>
                        </thead>
                        <tbody>
                            ${this._channelProducts.map(
                                (cp) => html`
                                <tr>
                                    <td><code>${cp.DiscordChannelID}</code></td>
                                    <td>${this._productName(cp.Product)} <span class="text-muted">(<code>${cp.Product}</code>)</span></td>
                                    <td class="text-end">
                                        ${
                                            this._confirmDelete === cp.ID
                                                ? html`
                                            <button class="btn btn-danger btn-sm" @click="${() => this._deleteChannelProduct(cp.ID)}">
                                                Confirm
                                            </button>
                                        `
                                                : html`
                                            <button class="btn btn-outline-danger btn-sm" @click="${() => (this._confirmDelete = cp.ID)}">
                                                <i class="bi bi-trash"></i>
                                            </button>
                                        `
                                        }
                                    </td>
                                </tr>
                            `,
                            )}
                        </tbody>
                    </table>
                `
                }
            `
            }
        `;
    }

    _renderForm() {
        return html`
            <div class="d-flex align-items-center gap-2 mb-3">
                <a href="/channel-products" class="btn btn-outline-secondary btn-sm">
                    <i class="bi bi-arrow-left"></i>
                </a>
                <h5 class="mb-0">Add Channel Product</h5>
            </div>

            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}

            <form @submit="${(e) => this._submitCreate(e)}" style="max-width: 500px;">
                <div class="mb-3">
                    <label class="form-label">Discord Channel ID</label>
                    <input type="text" class="form-control" placeholder="e.g. 1234567890"
                           .value="${this._formData.discord_channel_id}"
                           @input="${(e) => this._updateField('discord_channel_id', e.target.value)}"
                           required>
                    <div class="form-text">Enable Developer Mode in Discord settings, then right-click the channel and Copy Channel ID</div>
                </div>

                <div class="mb-3">
                    <label class="form-label">Product</label>
                    <select class="form-select"
                            .value="${this._formData.product}"
                            @change="${(e) => this._updateField('product', e.target.value)}"
                            required>
                        <option value="" disabled ?selected="${!this._formData.product}">Select a product…</option>
                        ${this._products.map(
                            (product) => html`
                            <option value="${product.slug}" ?selected="${this._formData.product === product.slug}">
                                ${product.name} (${product.slug})
                            </option>
                        `,
                        )}
                    </select>
                    <div class="form-text">The products come from the Dice Driven Community website. A channel maps to one; adding it again replaces it.</div>
                </div>

                <div class="d-flex gap-2">
                    <button type="submit" class="btn btn-primary" ?disabled="${this._saving}">
                        ${this._saving ? html`<span class="spinner-border spinner-border-sm"></span>` : 'Save'}
                    </button>
                    <a href="/channel-products" class="btn btn-outline-secondary">Cancel</a>
                </div>
            </form>
        `;
    }
}

customElements.define('channel-products-view', ChannelProductsView);
